import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  CalendarDaysIcon,
  CalendarRangeIcon,
  CheckIcon,
  FilterIcon,
  CalendarIcon,
  ClockIcon,
  StarIcon,
  ChevronDown,
} from "lucide-react";
import moment from "moment";
import { getDateRange } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SelectDateRange } from "./select-date-range-component";
import { Separator } from "./ui/separator";

interface SelectDateRangeProps {
  onDateRangeChange: (
    baseRange: { from: Date | undefined; to: Date | undefined },
    comparisonRange: { from: Date | undefined; to: Date | undefined }
  ) => void;
  initialBaseFrom?: Date;
  initialBaseTo?: Date;
  initialComparisonFrom?: Date;
  initialComparisonTo?: Date;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const SelectComparisonDateRange = ({
  open,
  setOpen,
  onDateRangeChange,
  initialBaseFrom,
  initialBaseTo,
  initialComparisonFrom,
  initialComparisonTo,
}: SelectDateRangeProps) => {
  const [baseRange, setBaseRange] = useState<{ from: Date | undefined; to: Date | undefined }>(
    initialBaseFrom && initialBaseTo
      ? { from: initialBaseFrom, to: initialBaseTo }
      : {
        from: moment().startOf("month").toDate(),
        to: moment().endOf("month").toDate(),
      }
  );

  const [comparisonRange, setComparisonRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>(
    initialComparisonFrom && initialComparisonTo
      ? { from: initialComparisonFrom, to: initialComparisonTo }
      : {
        from: moment().subtract(1, "month").startOf("month").toDate(),
        to: moment().subtract(1, "month").endOf("month").toDate(),
      }
  );

  const handleBaseRangeChange = (fromDate: Date, toDate: Date) => {
    setBaseRange({ from: fromDate, to: toDate });
  };

  const handleComparisonRangeChange = (fromDate: Date, toDate: Date) => {
    setComparisonRange({ from: fromDate, to: toDate });
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[625px]">
          <DialogHeader>
            <DialogTitle>Compare Two Time Periods</DialogTitle>
            <DialogDescription>
              Select two date ranges to compare metrics between different time periods
            </DialogDescription>
          </DialogHeader>

          <div className="w-full flex flex-col gap-8">
            <div className="p-4 border rounded-lg bg-muted/10">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="w-5 h-5 text-blue-500" />
                <Label className="font-medium text-lg">Primary Period</Label>
              </div>
              <SelectDateRange
                onDateRangeChange={handleBaseRangeChange}
                initialFromDate={baseRange.from}
                initialToDate={baseRange.to}
                initialRange={"custom"}
              />
            </div>

            <div className="relative">
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4">
                <span className="text-sm text-muted-foreground">vs</span>
              </div>
              <Separator />
            </div>

            <div className="p-4 border rounded-lg bg-muted/10">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="w-5 h-5 text-purple-500" />
                <Label className="font-medium text-lg">Comparison Period</Label>
              </div>
              <SelectDateRange
                onDateRangeChange={handleComparisonRangeChange}
                initialFromDate={comparisonRange.from}
                initialToDate={comparisonRange.to}
                initialRange={"custom"}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
              className="w-1/3"
            >
              Cancel
            </Button>
            <Button
              className="w-2/3 bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if(baseRange && comparisonRange) {
                  onDateRangeChange(baseRange, comparisonRange);
                }
              }}
              variant="secondary"
              disabled={!baseRange || !comparisonRange}
            >
              <CheckIcon className="w-4 h-4 mr-2" />
              Compare Periods
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
